import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AppButtonComponent } from '@hunting-nature/shared-ui/components/app-button/app-button.component';
import type { BottomSheetSettings } from '@hunting-nature/shared-ui/components/bottom-sheet/bottom-sheet.component';
import { BottomSheetComponent } from '@hunting-nature/shared-ui/components/bottom-sheet/bottom-sheet.component';
import { pickIcons } from '@hunting-nature/shared-ui/helper/icon-registry.const';
import { IonButtons, IonIcon } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { addIcons } from 'ionicons';

import { LiveStreamSelectors } from '../../live-stream/redux/live-stream.reducer';
import { HuntingProductsActions } from '../../redux/hunting-products.actions';
import { HuntingProductsSelectors } from '../../redux/hunting-products.reducer';
import { CannesWifiConnectSheetSelectors } from './redux/cannes-wifi-connect-sheet.selectors';

@Component({
  selector: 'app-cannes-wifi-connect-sheet',
  standalone: true,
  imports: [BottomSheetComponent, AppButtonComponent, IonButtons, IonIcon, TranslocoPipe],
  templateUrl: './cannes-wifi-connect-sheet.component.html',
  styleUrl: './cannes-wifi-connect-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CannesWifiConnectSheetComponent {
  // services
  private readonly store = inject(Store);

  // UI bindings
  protected readonly isOpen$$ = this.store.selectSignal(HuntingProductsSelectors.isCannesWifiSheetOpen);
  protected readonly isRetrying$$ = this.store.selectSignal(HuntingProductsSelectors.isCannesWifiRetrying);
  private readonly wifiDeviceId$$ = this.store.selectSignal(HuntingProductsSelectors.getCannesWifiDeviceId);
  protected readonly deviceName$$ = this.store.selectSignal(HuntingProductsSelectors.getConnectedDeviceName);
  protected readonly liveStreamError$$ = this.store.selectSignal(LiveStreamSelectors.error);

  /**
   * One sheet, one continuous progress story: joining the device AP, then (for the live-stream
   * intent) setting the stream up, then failure states for either phase. The former green
   * "connected" stopover is gone — the AP being up is not a goal, the stream (or sync) is.
   */
  protected readonly sheetPhase$$ = this.store.selectSignal(CannesWifiConnectSheetSelectors.getSheetPhase);

  protected readonly sheetSettings: BottomSheetSettings = {
    showDraggable: false,
    dragBy: [],
    preventClicks: false,
    initialBreak: 'middle',
    breaks: {
      top: { enabled: false },
      middle: { enabled: true, height: Math.min(440, Math.round(window.innerHeight * 0.65)), bounce: false },
      bottom: { enabled: false },
    },
  };

  constructor() {
    addIcons(pickIcons('hunting-connecting', 'hunting-setup-stream', 'hunting-wifi-connectivity'));
  }

  // UI handlers
  protected handleRetry(): void {
    if (this.isRetrying$$()) return;
    const deviceId = this.wifiDeviceId$$();
    if (!deviceId) return;
    this.store.dispatch(HuntingProductsActions.retryCannesWifiConnect({ deviceId }));
  }

  // Header X and sheet dismiss events alike — backing out drops the live-stream/sync intent.
  // Aborting the pending (or cleaning up the failed) stream start is handled in redux: the
  // cancelCannesWifiConnect effect tears the stream down when the sheet owned its setup phase.
  protected handleCancel(): void {
    if (!this.isOpen$$() || this.isRetrying$$()) return;
    this.store.dispatch(HuntingProductsActions.cancelCannesWifiConnect());
  }
}
